// astro vue will render empty page if import vue from 'vue'
// @ts-ignore
import { createApp } from 'vue/dist/vue.esm-bundler.js'
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from '@nanostores/vue'
import { isLogin } from '@/store/userStore'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import CouponCard from '@/components/CouponCard.vue'
import CouponCenterDialogSheet from '@/components/CouponCenterDialogSheet.vue'
import CouponTabs from '@/components/CouponTabs.vue'
import hybridPage from '@/lib/hybridPage'
import { connectExpressCheckout, fetchCoupons } from '@/assets/ts/apiManagement'
import {
	type ICoupon,
	type ICouponTab,
	type ApiOrderTypeValue,
} from '@/types/Coupon'
import { useBreakpoints } from '@/lib/utils'
import { createGAEvent, GaGeneralEvent } from '@/assets/ts/customEvent'
import { isExpressCheckoutEnabled } from '@/lib/checkService'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'
import PageBreadCrumb from '@/components/PageBreadCrumb.vue'

export enum CouponCenterTabsType {
	All = 'all',
	ProxyShopping = 'proxy-shopping',
	Shipping = 'shipping',
	ExpressCheckout = 'express-checkout',
	Campaign1 = 'tab1',
	Campaign2 = 'tab2',
	Campaign3 = 'tab3',
}

interface CouponCenterTabData {
	name: string
	value: CouponCenterTabsType
}

const orderTypeToTabMap: Record<ApiOrderTypeValue, CouponCenterTabsType> = {
	shipment: CouponCenterTabsType.Shipping,
	b4u: CouponCenterTabsType.ProxyShopping,
	bnshop: CouponCenterTabsType.ExpressCheckout,
}

const pageNameMap: Record<CouponCenterTabsType, string> = {
	[CouponCenterTabsType.All]: 'Coupon Centre Page',
	[CouponCenterTabsType.ProxyShopping]: 'Coupon Centre Page - Buyforyou',
	[CouponCenterTabsType.Shipping]: 'Coupon Centre Page - Shipment',
	[CouponCenterTabsType.ExpressCheckout]: 'Coupon Centre Page - EC',
	[CouponCenterTabsType.Campaign1]: 'Coupon Centre Page - Campaign 1',
	[CouponCenterTabsType.Campaign2]: 'Coupon Centre Page - Campaign 2',
	[CouponCenterTabsType.Campaign3]: 'Coupon Centre Page - Campaign 3',
}

hybridPage()

createApp({
	name: 'ExpressCheckout',
	components: {
		BaseButton,
		CouponCard,
		CouponCenterDialogSheet,
		CouponTabs,
		PageBreadCrumb,
	},
	setup() {
		const heroContentData = window.pageContent.heroContent
		const isLoginGlobal = useStore(isLogin)

		const {
			couponCardText,
			couponCenterTabsData,
		}: {
			couponCardText: any
			couponCenterTabsData: CouponCenterTabData[]
		} = window.pageContent
		const { isLarge } = useBreakpoints()

		const allCoupons = ref<ICoupon[]>([])
		const campaignTabs = ref<ICouponTab[]>([])
		const filteredCoupons = ref<ICoupon[]>([])
		const currentCoupon = ref<ICoupon | null>(null)
		const isOpen = ref(false)
		const isLoading = ref(false)
		const isPageLoadInited = ref(false)
		const activeTab = ref<CouponCenterTabsType>(CouponCenterTabsType.All)

		const currentPageName = computed(
			() =>
				pageNameMap[activeTab.value] ||
				`Coupon Centre Dynamic Page - ${activeTab.value}`,
		)
		const usage = computed(() => {
			if (!currentCoupon.value)
				return couponCardText.coupon_details_product_express
			const orderTypeToUsageMap = {
				bnshop: couponCardText.coupon_details_product_express,
				b4u: couponCardText.coupon_details_product_proxy,
				shipment: couponCardText.coupon_details_product_shipping,
			}
			return (
				orderTypeToUsageMap[currentCoupon.value.order_type] ||
				couponCardText.coupon_details_product_express
			)
		})
		const offersURL = computed(() => getOffersURL())
		const visibleTabs = computed(() => {
			// Get base tabs (All, Express Checkout, Proxy Shopping, Shipping)
			const getBaseTabs = () => {
				const isECEnabled = isExpressCheckoutEnabled()

				const isBaseTab = (value: CouponCenterTabsType) => {
					return (
						value === CouponCenterTabsType.All ||
						value === CouponCenterTabsType.ProxyShopping ||
						value === CouponCenterTabsType.Shipping ||
						(value === CouponCenterTabsType.ExpressCheckout && isECEnabled)
					)
				}

				return couponCenterTabsData
					.filter(tab => isBaseTab(tab.value))
					.map(tab => ({
						...tab,
						url: tab.value,
					}))
			}

			// Get campaign tabs sorted by order
			const getCampaignTabs = () => {
				return campaignTabs.value
					.sort((a, b) => a.order - b.order)
					.map(campaign => {
						const matchingTab = couponCenterTabsData.find(
							tab => tab.value === campaign.tab_key,
						)
						if (!matchingTab) return null

						return {
							...matchingTab,
							url: campaign.url_postfix,
						}
					})
					.filter(Boolean)
			}

			const baseTabs = getBaseTabs()
			const orderedCampaignTabs = getCampaignTabs()

			return [...baseTabs, ...orderedCampaignTabs]
		})

		const breadcrumbs = computed(() => [
			{
				displayName: window.pageContent.breadcrumbs?.home || '',
				categoryId: 'home',
				link: getSiteBaseUrl(),
			},
			{
				displayName: window.pageContent.breadcrumbs?.coupons || '',
				categoryId: 'coupons',
				link: `${getSiteBaseUrl()}/coupons/`,
			},
		])

		const showModal = (coupon: ICoupon) => {
			currentCoupon.value = coupon
			isOpen.value = true
		}

		const updateIsOpen = (value: boolean) => {
			isOpen.value = value
		}

		const getOffersURL = () => {
			const baseURL = getSiteBaseUrl()
			const {
				current_country: currentCountry,
				current_language: currentLanguage,
			} = window.BUYANDSHIP.settings
			const isHongKongChinese =
				currentCountry.toLowerCase() === 'hk' &&
				currentLanguage.toLowerCase() === 'zh_hk'
			const categoryPath = isHongKongChinese
				? '%E6%9C%83%E5%93%A1%E5%84%AA%E6%83%A0'
				: 'offers'
			return `${baseURL}/blog/categories/${categoryPath}/`
		}

		const updateActiveTab = (tab: CouponCenterTabsType) => {
			if (activeTab.value === tab && isPageLoadInited.value) {
				return
			}
			activeTab.value = tab
			filterCoupons()
			createGAEvent(GaGeneralEvent.PageLoad, {
				page_name: currentPageName.value,
			})
			isPageLoadInited.value = true
		}

		const filterCoupons = () => {
			const isCampaignTab = (currentTab: CouponCenterTabsType) =>
				campaignTabs.value.some(tab => tab.tab_key === currentTab)

			const filterByCampaign = (coupon: ICoupon) =>
				coupon.tabs.includes(activeTab.value)

			const isInDynamicTab = (coupon: ICoupon) =>
				coupon.tabs.some(tabKey =>
					campaignTabs.value.some(ct => ct.tab_key === tabKey),
				)

			const filterByOrderType = (coupon: ICoupon) =>
				orderTypeToTabMap[coupon.order_type] === activeTab.value &&
				!isInDynamicTab(coupon)

			// Apply filters based on active tab
			filteredCoupons.value =
				activeTab.value === CouponCenterTabsType.All
					? allCoupons.value
					: allCoupons.value.filter(
							isCampaignTab(activeTab.value)
								? filterByCampaign
								: filterByOrderType,
					  )
		}

		const getCurrentTab = (): CouponCenterTabsType => {
			const isProd = import.meta.env.PROD
			let tabParam: string | null = null

			if (isProd) {
				const pathParts = window.location.pathname.split('/').filter(Boolean)
				tabParam = pathParts[pathParts.indexOf('coupons') + 1]
			} else {
				const url = new URL(window.location.href)
				tabParam = url.searchParams.get('tab')
			}

			const matchingTab = visibleTabs.value.find(tab => tab?.url === tabParam)
			if (matchingTab) {
				return matchingTab.value
			}

			redirectToDefaultTab()
			return CouponCenterTabsType.All
		}

		const redirectToDefaultTab = () => {
			const isProd = import.meta.env.PROD
			const baseUrl = isProd
				? `${getSiteBaseUrl()}/coupons/all/`
				: `/coupons?tab=all`
			window.history.replaceState(null, '', baseUrl)
		}

		const postGAButtonClick = (buttonName: string) => {
			createGAEvent(GaGeneralEvent.ButtonClick, {
				page_name: currentPageName.value,
				button_name: buttonName,
			})
		}

		const handleCouponEmptyButtonClick = () => {
			postGAButtonClick('Go To Coupon Offers')
		}

		const handleFindMoreOffersButtonClick = () => {
			postGAButtonClick('More Coupon Offers')
		}

		const initializeCoupons = async () => {
			try {
				isLoading.value = true

				// Fetch express checkout token if logged in and EC is available in this region
				// to ensure real-time EC coupon updates
				if (isLoginGlobal.value && isExpressCheckoutEnabled()) {
					await connectExpressCheckout()
				}

				const { coupon_list: couponList, tabs } = await fetchCoupons()
				allCoupons.value = couponList
				campaignTabs.value = tabs

				const currentTab = getCurrentTab()
				updateActiveTab(currentTab)

				filterCoupons()
			} catch (error) {
				console.error('Failed to fetch coupons:', error)
			} finally {
				isLoading.value = false
			}
		}

		onMounted(() => {
			initializeCoupons()
		})

		watch(
			() => window.location.href,
			() => {
				const currentTab = getCurrentTab()
				updateActiveTab(currentTab)
			},
		)

		watch(activeTab, () => {
			filterCoupons()
		})

		return {
			heroContentData,
			couponCardText,
			couponCenterTabsData,
			allCoupons,
			filteredCoupons,
			currentCoupon,
			isOpen,
			showModal,
			usage,
			updateIsOpen,
			isLarge,
			offersURL,
			isLoading,
			activeTab,
			updateActiveTab,
			currentPageName,
			handleCouponEmptyButtonClick,
			handleFindMoreOffersButtonClick,
			visibleTabs,
			breadcrumbs,
		}
	},
}).mount('#bsMain')
